import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import axios from "axios";
import "./UserManagement.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "associate",
    isActive: true,
    ptoLimit: 0,
    isFullTime: true,
    phoneNumber: "",
    employmentStartDate: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data.users);
    } catch (error) {
      console.error("Error fetching users:", error);
      alert("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: "",
        role: user.role,
        isActive: user.isActive,
        ptoLimit: user.ptoLimit || 0,
        isFullTime: user.isFullTime !== undefined ? user.isFullTime : true,
        phoneNumber: user.phoneNumber || "",
        employmentStartDate: user.employmentStartDate || "",
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "associate",
        isActive: true,
        ptoLimit: 0,
        isFullTime: true,
        phoneNumber: "",
        employmentStartDate: "",
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "associate",
      isActive: true,
      ptoLimit: 0,
      isFullTime: true,
      phoneNumber: "",
      employmentStartDate: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        await axios.put(`${API_URL}/users/${editingUser.id}`, updateData);
        alert("User updated successfully!");
      } else {
        // Create user
        if (!formData.password) {
          alert("Password is required for new users");
          return;
        }
        await axios.post(`${API_URL}/users`, formData);
        alert("User created successfully!");
      }

      handleCloseModal();
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this user?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/users/${id}`);
      alert("User deactivated successfully!");
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to deactivate user");
    }
  };

  const handleActivate = async (id) => {
    try {
      await axios.patch(`${API_URL}/users/${id}/activate`);
      alert("User activated successfully!");
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to activate user");
    }
  };

  const handlePermanentDelete = async (id) => {
    if (
      !window.confirm(
        "⚠️ WARNING: This will permanently delete the user and ALL their data (timesheets, PTOs, etc.). This action CANNOT be undone!\n\nAre you absolutely sure?",
      )
    ) {
      return;
    }

    // Double confirmation
    if (
      !window.confirm(
        "This is your final warning. Type DELETE to confirm.\n\nClick OK to permanently delete this user.",
      )
    ) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/users/${id}/permanent`);
      alert("User permanently deleted!");
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete user");
    }
  };

  return (
    <Layout>
      <div className="user-management">
        <div className="page-header">
          <h1>User Management</h1>
          <button className="btn-primary" onClick={() => handleOpenModal()}>
            + Add New User
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>PTO Limit (hrs)</th>
                  <th>Employment Status</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Start Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="no-data">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.phoneNumber || "—"}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>{user.ptoLimit || 0}</td>
                      <td>
                        <span
                          className={`status-badge ${user.isFullTime !== false ? "active" : "inactive"}`}
                        >
                          {user.isFullTime !== false
                            ? "Full-Time"
                            : "Part-Time"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${user.isActive ? "active" : "inactive"}`}
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        {/* Date-only value: parse as local time so it doesn't shift a day */}
                        {user.employmentStartDate
                          ? new Date(
                              `${user.employmentStartDate}T00:00:00`,
                            ).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-small btn-edit"
                            onClick={() => handleOpenModal(user)}
                          >
                            Edit
                          </button>
                          {user.isActive ? (
                            <button
                              className="btn-small btn-delete"
                              onClick={() => handleDelete(user.id)}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              className="btn-small btn-activate"
                              onClick={() => handleActivate(user.id)}
                            >
                              Activate
                            </button>
                          )}
                          <button
                            className="btn-small btn-danger"
                            onClick={() => handlePermanentDelete(user.id)}
                            style={{
                              backgroundColor: "#d32f2f",
                              color: "white",
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal for Create/Edit User */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingUser ? "Edit User" : "Create New User"}</h2>
                <button className="modal-close" onClick={handleCloseModal}>
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit} className="user-form">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, phoneNumber: e.target.value })
                    }
                    placeholder="+1XXXXXXXXXX"
                    pattern="^\+1[2-9]\d{9}$"
                    title="US/Canada phone number in E.164 format (e.g. +12125551234)"
                  />
                  <small style={{ color: "#666", fontSize: "12px" }}>
                    US/Canada format: +1 followed by 10 digits (e.g.
                    +12125551234)
                  </small>
                </div>
                <div className="form-group">
                  <label>
                    Password{" "}
                    {editingUser ? "(leave blank to keep current)" : "*"}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required={!editingUser}
                  />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    required
                  >
                    <option value="associate">Associate</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>PTO Limit (hours per year)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.ptoLimit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        ptoLimit: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                  <small style={{ color: "#666", fontSize: "12px" }}>
                    Set the annual PTO allowance for this user (in hours)
                  </small>
                </div>
                <div className="form-group">
                  <label>Employment Start Date</label>
                  <input
                    type="date"
                    value={formData.employmentStartDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employmentStartDate: e.target.value,
                      })
                    }
                  />
                  <small style={{ color: "#666", fontSize: "12px" }}>
                    PTO accrues weekly from this date; leave blank to accrue
                    from Jan 1
                  </small>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isFullTime}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isFullTime: e.target.checked,
                        })
                      }
                    />
                    Full-Time Employee
                  </label>
                  <small
                    style={{
                      color: "#666",
                      fontSize: "12px",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    Full-time employees do not have holidays deducted from PTO
                    balance
                  </small>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                    Active
                  </label>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingUser ? "Update User" : "Create User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserManagement;
